import { observable, computed, reaction, makeObservable } from 'mobx'
import Document from './Document'
import {
  DocumentJobAccessEditPermission,
  DocumentAccessDTO,
} from './dtos/document-access'
import { Model } from 'infra/model'
import { Id } from 'types/definitions'
import { DateZero, maybeParseDate } from 'utils/date-util'
import { PrimeMember, GetMember } from 'iam/MemberStore'
import { GetJob } from 'jobs/JobStore'
import { GetDocument } from 'documents/DocumentStore'
import Job from 'jobs/Job'
import Member from 'iam/models/Member'
import once from 'lodash/once'

export type RefreshDocument = (documentLongId: Id) => unknown

export default class DocumentAccess extends Model<
  DocumentAccessDTO,
  DocumentAccess
> {
  @observable id = Id.none
  @observable grantorId = Id.none
  @observable grantorPublicId = Id.none
  @observable type = 'Job' as const
  @observable jobId = Id.none
  @observable editPermission: DocumentJobAccessEditPermission = 'Owner'
  @observable dateCreated = DateZero()
  @observable dateModified = DateZero()
  @observable documentId = Id.none
  @observable documentLongId = Id.none

  static get none() {
    return lazyNone()
  }

  constructor(
    primeMember: PrimeMember,
    private getMember: GetMember,
    private getJob: GetJob,
    private getDocument: GetDocument,
    private refreshDocument: RefreshDocument,
  ) {
    super()
    makeObservable(this)
    // When the access changes, refetch the document
    // so we get the latest `can_edit` value.
    // Refetch the document when edit permissions change
    reaction(
      () => this.editPermission,
      () => this.documentLongId && this.refreshDocument(this.documentLongId),
      { delay: 500 },
    )
    // Fetch the member once the document has been linked.
    reaction(
      (): [Document, Id, Id] => [
        this.document,
        this.grantorId,
        this.grantorPublicId,
      ],
      ([doc, grantorId, grantorPublicId]) =>
        primeMember({
          workspace_id: doc.workspaceId,
          user_id: grantorId,
          user_public_id: grantorPublicId,
        }),
    )
  }

  @computed
  get job(): Job | undefined {
    return this.getJob(this.jobId)
  }

  @computed
  get document(): Document {
    return this.getDocument(this.documentLongId) || Document.none
  }

  @computed
  get grantor() {
    if (!this.document) {
      return Member.none
    }
    return (
      this.getMember(this.document.workspaceId, this.grantorId) || Member.none
    )
  }

  parse(json: Partial<DocumentAccessDTO>): Partial<DocumentAccess> {
    return {
      id: json.id,
      grantorId: json.grantor_id,
      grantorPublicId: json.grantor_public_id,
      type: json.type,
      jobId: json.job_id,
      editPermission: json.edit_permission,
      dateCreated: maybeParseDate(json.date_created),
      dateModified: maybeParseDate(json.date_modified),
      documentId: json.document_id,
      documentLongId: json.document_long_id,
    }
  }
}

const lazyNone = once(
  () =>
    new DocumentAccess(
      () => Member.none,
      () => Member.none,
      () => Job.none,
      () => Document.none,
      async () => Document.none,
    ),
)
